import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Buildings, CheckCircle, XCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import QuoteDetails, { QuoteStatusBadge, eur, fmtDate } from '../components/QuoteDetails';

const Actions = ({ q, onDone }) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const respond = async (action) => {
    try {
      await api.post(`/api/quotes/${q.quote_id}/respond`, { action, reason });
      toast.success(action === 'accept' ? 'Preventivo accettato' : 'Preventivo rifiutato');
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'Operazione non riuscita'); }
  };
  if (rejecting) {
    return (
      <div className="space-y-2">
        <Textarea rows={2} placeholder="Motivo (facoltativo)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid={`quote-reject-reason-${q.quote_id}`} />
        <div className="flex gap-2">
          <Button variant="destructive" size="sm" onClick={() => respond('reject')} data-testid={`quote-reject-confirm-${q.quote_id}`}>Conferma rifiuto</Button>
          <Button variant="ghost" size="sm" onClick={() => setRejecting(false)} data-testid={`quote-reject-back-${q.quote_id}`}>Indietro</Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex gap-2">
      <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-full" onClick={() => respond('accept')} data-testid={`quote-accept-${q.quote_id}`}><CheckCircle size={16} className="mr-1" />Accetta</Button>
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setRejecting(true)} data-testid={`quote-reject-${q.quote_id}`}><XCircle size={16} className="mr-1" />Rifiuta</Button>
    </div>
  );
};

export default function ClientQuotesPage() {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const r = await api.get('/api/quotes'); setQuotes(r.data.items || []); }
    catch { toast.error('Errore nel caricamento dei preventivi'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-8 max-w-4xl space-y-6" data-testid="client-quotes-page">
      <div>
        <h2 className="font-heading text-2xl font-semibold">I miei preventivi</h2>
        <p className="text-sm text-slate-500">I preventivi che i Centri ti hanno inviato per le tue richieste.</p>
      </div>
      {loading ? <p className="text-sm text-slate-500">Caricamento…</p> : quotes.length === 0 ? (
        <Card className="p-8 text-center text-slate-500" data-testid="quotes-empty">Non hai ancora ricevuto preventivi.</Card>
      ) : quotes.map((q) => (
        <Card key={q.quote_id} className="p-5 space-y-4" data-testid={`quote-card-${q.quote_id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <Buildings size={24} className="text-primary" />
            <div className="flex-1 min-w-0">
              <div className="font-medium">{q.center_name} · Preventivo n. {q.number}</div>
              <div className="text-xs text-slate-500">{q.service} · ricevuto il {fmtDate(q.created_at)} · valido fino al {fmtDate(q.valid_until)}</div>
            </div>
            <div className="font-heading font-semibold">{eur(q.total)}</div>
            <QuoteStatusBadge status={q.status} id={q.quote_id} />
          </div>
          <QuoteDetails quote={q} />
          {q.status === 'sent' && <Actions q={q} onDone={load} />}
        </Card>
      ))}
    </div>
  );
}
